import React, { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Minus } from "lucide-react";
import { cn } from "@/lib/utils";

interface QuantityInputProps {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  disabled?: boolean;
  size?: "sm" | "md" | "lg";
  variant?: "default" | "compact";
  className?: string;
}

export const QuantityInput: React.FC<QuantityInputProps> = ({
  value,
  onChange,
  min = 1,
  max = 10000, // Much higher reasonable limit
  disabled = false,
  size = "md",
  variant = "default",
  className,
}) => {
  const [inputValue, setInputValue] = useState(value.toString());
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;

    // Allow empty string and numeric input
    if (newValue === "") {
      setInputValue(newValue);
    } else if (/^\d+$/.test(newValue)) {
      const numValue = parseInt(newValue);

      // Always update the input value to allow typing
      setInputValue(newValue);

      // Only call onChange if we have a valid positive number within limits
      if (numValue > 0 && numValue <= max) {
        onChange(numValue);
      }
      // If above max, still allow typing but don't call onChange until blur
    }
  };

  const handleInputFocus = () => {
    setIsFocused(true);
    // Select all text when focused for easy replacement
    setTimeout(() => {
      inputRef.current?.select();
    }, 0);
  };

  const handleInputBlur = () => {
    setIsFocused(false);

    // On blur, ensure we have a valid value
    const numValue = parseInt(inputValue);
    let finalValue = numValue;

    // If empty or invalid, use the minimum value
    if (isNaN(numValue) || numValue < min) {
      finalValue = min;
    }
    // Apply max limit only if it's reasonable
    else if (max && numValue > max) {
      finalValue = max;
    }

    setInputValue(finalValue.toString());
    if (finalValue !== value) {
      onChange(finalValue);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.currentTarget.blur();
    }
    // Allow common editing keys
    if (
      ["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(e.key)
    ) {
      return;
    }
    // Block non-numeric keys except for selection shortcuts
    if (!/^\d$/.test(e.key) && !e.ctrlKey && !e.metaKey) {
      e.preventDefault();
    }
  };

  const handleDecrement = () => {
    const newValue = Math.max(min, value - 1);
    setInputValue(newValue.toString());
    onChange(newValue);
  };

  const handleIncrement = () => {
    const newValue = value + 1;
    setInputValue(newValue.toString());
    onChange(newValue);
  };

  // Sync external value changes only when not focused
  React.useEffect(() => {
    if (!isFocused && value.toString() !== inputValue) {
      setInputValue(value.toString());
    }
  }, [value, inputValue, isFocused]);

  // Size variants
  const sizeClasses = {
    sm: {
      button: "h-8 w-8 p-0",
      input: "h-8 text-sm",
      icon: "h-3 w-3",
    },
    md: {
      button: "h-10 w-10 p-0",
      input: "h-10 text-base",
      icon: "h-4 w-4",
    },
    lg: {
      button: "h-12 w-12 p-0",
      input: "h-12 text-lg",
      icon: "h-5 w-5",
    },
  };

  const currentSize = sizeClasses[size];

  if (variant === "compact") {
    return (
      <div className={cn("flex items-center gap-1", className)}>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleDecrement}
          disabled={disabled || value <= min}
          className={currentSize.button}
        >
          <Minus className={currentSize.icon} />
        </Button>

        <Input
          ref={inputRef}
          type="text"
          inputMode="numeric"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          className={cn(
            "min-w-[2.5rem] max-w-[5rem] text-center font-medium border-0 bg-transparent px-1 shadow-none focus-visible:ring-0",
            currentSize.input,
          )}
          style={{
            width: `${Math.max(2.5, Math.min(5, inputValue.length * 0.75 + 1.5))}rem`,
          }}
        />

        {value >= max ? (
          <div
            className={cn(
              "flex items-center justify-center font-medium text-gray-500 text-xs",
              currentSize.button,
            )}
          >
            max
          </div>
        ) : (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleIncrement}
            disabled={disabled}
            className={currentSize.button}
          >
            <Plus className={currentSize.icon} />
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className={cn("flex items-center gap-2", className)}>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={handleDecrement}
        disabled={disabled || value <= min}
        className={currentSize.button}
      >
        <Minus className={currentSize.icon} />
      </Button>

      <Input
        ref={inputRef}
        type="text"
        inputMode="numeric"
        value={inputValue}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        className={cn(
          "min-w-[2.5rem] max-w-[5rem] text-center font-medium",
          currentSize.input,
          className,
        )}
        style={{
          width: `${Math.max(2.5, Math.min(5, inputValue.length * 0.75 + 1.5))}rem`,
        }}
      />

      {value >= max ? (
        <div
          className={cn(
            "flex items-center justify-center font-medium text-gray-500 text-xs",
            currentSize.button,
          )}
        >
          max
        </div>
      ) : (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleIncrement}
          disabled={disabled}
          className={currentSize.button}
        >
          <Plus className={currentSize.icon} />
        </Button>
      )}
    </div>
  );
};
